/**
 * Centralized Anti-Forgery Token Setup for VBS
 * 
 * Automatically attaches the RequestVerificationToken header to all
 * jQuery $.ajax() and native fetch() calls that use unsafe HTTP methods
 * (POST, PUT, DELETE, PATCH).
 *
 * Token source (priority order):
 *   1. <meta name="csrf-token"> tag (preferred, set by _AntiForgeryMeta.cshtml)
 *   2. <input name="__RequestVerificationToken"> hidden field (fallback for forms)
 *
 * If neither source is available, the call proceeds WITHOUT the token header.
 * This prevents breakage on pages that legitimately don't need CSRF protection
 * (e.g., public pages, API-only pages with JWT auth).
 */
(function () {
    'use strict';

    // ---------------------------------------------------------------
    // Token Retrieval (with graceful fallback)
    // ---------------------------------------------------------------

    /**
     * Returns the anti-forgery token value, or null if not available.
     */
    function getAntiForgeryToken() {
        // Priority 1: <meta name="csrf-token"> (layout-level, always present on authenticated pages)
        var metaTag = document.querySelector('meta[name="csrf-token"]');
        if (metaTag && metaTag.content) {
            return metaTag.content;
        }

        // Priority 2: hidden input from @Html.AntiForgeryToken() inside a <form>
        var hiddenInput = document.querySelector('input[name="__RequestVerificationToken"]');
        if (hiddenInput && hiddenInput.value) {
            return hiddenInput.value;
        }

        // Token not available — return null (caller decides what to do)
        return null;
    }

    // Expose globally so existing code can migrate gradually
    // Replaces the per-layout getToken() and per-file getRequestToken()
    window.getAntiForgeryToken = getAntiForgeryToken;

    // Keep backward compatibility with existing getToken() calls
    // (only override if not already defined by a layout — layouts load after this)
    // This is handled in Step 3 below.

    // ---------------------------------------------------------------
    // HTTP methods that require CSRF protection
    // ---------------------------------------------------------------

    var UNSAFE_METHODS = ['POST', 'PUT', 'DELETE', 'PATCH'];

    function isUnsafeMethod(method) {
        return UNSAFE_METHODS.indexOf((method || 'GET').toUpperCase()) !== -1;
    }

    // ---------------------------------------------------------------
    // jQuery $.ajax() — Global prefilter
    // ---------------------------------------------------------------

    if (typeof jQuery !== 'undefined') {
        // $.ajaxPrefilter runs BEFORE every $.ajax request.
        // It can modify options before the request is sent.
        jQuery.ajaxPrefilter(function (options) {
            // Only add to unsafe methods (POST, PUT, DELETE, PATCH)
            if (!isUnsafeMethod(options.type || options.method)) {
                return;
            }

            // Don't add if already manually set (respect explicit headers)
            if (options.headers && options.headers.RequestVerificationToken) {
                return;
            }

            var token = getAntiForgeryToken();
            if (token) {
                // Ensure headers object exists
                options.headers = options.headers || {};
                options.headers.RequestVerificationToken = token;
            }
            // If token is null, proceed without it — the server will reject
            // if [ValidateAntiForgeryToken] is on the endpoint, which is the
            // correct behavior (fail loudly rather than silently).
        });
    }

    // ---------------------------------------------------------------
    // Native fetch() — Wrapper / Monkey-patch
    // ---------------------------------------------------------------

    // Only patch if fetch exists (it does in all modern browsers)
    if (typeof window.fetch === 'function') {
        var originalFetch = window.fetch;

        window.fetch = function (input, init) {
            init = init || {};

            // Determine the HTTP method
            var method = init.method || 'GET';

            // Only add to unsafe methods
            if (!isUnsafeMethod(method)) {
                return originalFetch.call(this, input, init);
            }

            // Build headers — handle both Headers object and plain object
            var headers;
            if (init.headers instanceof Headers) {
                headers = init.headers;
            } else {
                headers = new Headers(init.headers || {});
            }

            // Don't overwrite if already set
            if (!headers.has('RequestVerificationToken')) {
                var token = getAntiForgeryToken();
                if (token) {
                    headers.set('RequestVerificationToken', token);
                }
            }

            // Reconstruct init with the (possibly modified) headers
            init.headers = headers;

            return originalFetch.call(this, input, init);
        };
    }

})();